import React, { ChangeEvent, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CardMedia,
  IconButton,
  Input,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useGetSettings } from "../../api/hooks/useSettings";
import { uploadService } from "../../api/services/uploadService";
import { useProgramAdd, useGetWorkouts, useGetRefWorkouts, useGetPrograms } from "../../api/hooks";
import { showError, showSuccess } from "../../helpers/toast";
import { WORKOUT_ATTRIBUTES, DIFFICULTY_LEVEL, VISIBILITY_SCOPE, WEEKLY_PROGRAM_STATUS } from "../../constants/settings";
import { getQueryClient } from "../../api/queryClient";
import { queryTags } from "../../api/queryTags";
import EditText from "../../components/editText";
import { isQuillEmpty } from "../../helpers/validations";

interface WeekDay {
  day: number;
  workoutId: string;
}

interface ProgramWeek {
  week: number;
  days: WeekDay[];
  status: number;
  unlockDate: string;
}

const AddProgram = () => {
  const navigate = useNavigate();

  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [difficultyLevel, setDifficultyLevel] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<string[]>([]);
  const [goalOutcome, setGoalOutcome] = useState<string[]>([]);
  const [visibilityScope, setVisibilityScope] = useState("");
  const [isPublished, setIsPublished] = useState(false);

  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");
  const [uniqueId, setUniqueId] = useState("");
  const [durationInWeeks, setDurationInWeeks] = useState("");
  const [daysPerWeek, setDaysPerWeek] = useState("");
  const [description, setDescription] = useState("");

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { data: existingProgramsData } = useGetPrograms({ page: 1, limit: 1000 });

  const generateUniqueIdValue = () => {
    const rawList = existingProgramsData?.data;
    let list: any[] = [];
    if (Array.isArray(rawList)) {
      list = rawList;
    } else if (rawList && typeof rawList === "object" && Array.isArray((rawList as any).list)) {
      list = (rawList as any).list;
    }
    const usedIds = new Set<string>(
      list.map((p) => (p.uniqueId || "").trim().toUpperCase()).filter(Boolean)
    );

    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const all = chars + nums;

    let result = "";
    let attempts = 0;
    while (attempts < 1000) {
      attempts++;
      let temp = "";
      let hasChar = false;
      let hasNum = false;
      for (let i = 0; i < 6; i++) {
        const char = all.charAt(Math.floor(Math.random() * all.length));
        if (/[A-Z]/.test(char)) hasChar = true;
        if (/[0-9]/.test(char)) hasNum = true;
        temp += char;
      }
      if (!hasChar || !hasNum) continue;
      if (!usedIds.has(temp.toUpperCase()) && temp.toUpperCase() !== uniqueId.trim().toUpperCase()) {
        result = temp;
        break;
      }
    }
    return result || "A1B2C3";
  };

  const handleGenerateUniqueId = () => {
    const code = generateUniqueIdValue();
    setUniqueId(code);
    if (errors.uniqueId) setErrors((prev) => ({ ...prev, uniqueId: "" }));
  };

  const { mutate: createProgram, isPending: isCreating } = useProgramAdd({
    onSuccess: (response) => {
      showSuccess(response.message || "Program created successfully");
      getQueryClient().invalidateQueries({ queryKey: [queryTags.workout] });
      setTimeout(() => {
        navigate("/manage-programs");
      }, 100);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to create badge");
    },
  });

  const [weeks, setWeeks] = useState<ProgramWeek[]>([
    {
      week: 1,
      days: [{ day: 1, workoutId: "" }],
      status: WEEKLY_PROGRAM_STATUS.UNLOCK,
      unlockDate: "",
    },
  ]);

  const handleDifficultyLevel = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const next = typeof value === "string" ? value.split(",") : (value as string[]);
    setDifficultyLevel(next);
    if (errors.difficultyLevel) setErrors((prev) => ({ ...prev, difficultyLevel: "" }));
  };

  const handleVisibilityScope = (event: SelectChangeEvent) => {
    setVisibilityScope(event.target.value as string);
    if (errors.visibilityScope) setErrors((prev) => ({ ...prev, visibilityScope: "" }));
  };

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.includes("image")) {
        setFileName(file.name);
        setImageFile(file);
        const previewUrl = URL.createObjectURL(file);
        setImage(previewUrl);

        // Upload image to backend immediately
        try {
          setUploadingImage(true);
          const formData = new FormData();
          formData.append("file", file);
          const uploadResponse = await uploadService.uploadFile(formData);

          let uploadedImageUrl = "";

          if (uploadResponse?.data) {
            // Handle different response formats
            if (typeof uploadResponse.data === "string") {
              uploadedImageUrl = uploadResponse.data;
            } else if (
              uploadResponse.data &&
              typeof uploadResponse.data === "object"
            ) {
              const dataObj = uploadResponse.data as any;
              uploadedImageUrl =
                dataObj.url || dataObj.image || dataObj.path || "";
            }
          }

          if (!uploadedImageUrl) {
            // Handle upload failure
            setImage("");
            setImageFile(null);
            setFileName("");
            return;
          }

          // Update image with uploaded URL
          setImage(uploadedImageUrl);
          // Clear the file reference since we now have the URL
          setImageFile(null);
        } catch (err: any) {
          // Handle error
          setImage("");
          setImageFile(null);
          setFileName("");
        } finally {
          setUploadingImage(false);
        }
      }
    }
  };

  // Validation functions
  const validateTitle = (value: string) => {
    if (!value.trim()) return "Title is required";
    if (value.trim().length < 3) return "Title must be at least 3 characters";
    return "";
  };

  const validateUniqueId = (value: string) => {
    if (!value.trim()) return "Unique Id is required (click Auto Generate)";
    if (value.trim().length !== 6) return "Unique Id must be exactly 6 characters";
    if (!/[a-zA-Z]/.test(value) || !/[0-9]/.test(value)) {
      return "Unique Id must include both characters and numbers";
    }
    return "";
  };

  const validateDurationInWeeks = (value: string) => {
    // Optional when creating/updating: allow saving without entering weeks/workouts.
    if (!value.trim()) return "";
    const num = parseInt(value);
    if (isNaN(num) || num < 1) return "Duration must be a positive number";
    return "";
  };

  const validateDaysPerWeek = (value: string) => {
    // Optional when creating/updating: allow saving without entering weeks/workouts.
    if (!value.trim()) return "";
    const num = parseInt(value);
    if (isNaN(num) || num < 1 || num > 7) return "Days per week must be between 1 and 7";
    return "";
  };

  const validateDifficultyLevel = (value: string[]) => {
    if (!value || value.length === 0) return "Difficulty level is required";
    return "";
  };

  const validateEquipment = (value: string[]) => {
    if (value.length === 0) return "Equipment is required";
    return "";
  };

  const validateGoalOutcome = (value: string[]) => {
    if (value.length === 0) return "Goal outcome is required";
    return "";
  };

  const validateVisibilityScope = (value: string) => {
    if (!value) return "Visibility scope is required";
    return "";
  };

  const validateDescription = (value: string) => {
    if (isQuillEmpty(value)) return "Description is required";

    const clean = value
      .replace(/<(.|\n)*?>/g, "")
      .trim();

    if (clean.length < 10) return "Description must be at least 10 characters";
    return "";
  };

  const validateImage = () => {
    if (!image) return "Thumbnail is required";
    return "";
  };

  const validateWeeks = () => {
    // Weekly schedule is optional.
    // We only keep lightweight constraints so the UI won't block submissions.
    for (const week of weeks) {
      if (week.days.length > 7) return "Each week can have at most 7 days";
    }
    return "";
  };

  const validateAllFields = (customUniqueId?: string): boolean => {
    const newErrors: { [key: string]: string } = {};

    const titleError = validateTitle(title);
    if (titleError) newErrors.title = titleError;

    const idToValidate = customUniqueId !== undefined ? customUniqueId : uniqueId;
    const uniqueIdError = validateUniqueId(idToValidate);
    if (uniqueIdError) newErrors.uniqueId = uniqueIdError;

    const durationError = validateDurationInWeeks(durationInWeeks);
    if (durationError) newErrors.durationInWeeks = durationError;

    const daysPerWeekError = validateDaysPerWeek(daysPerWeek);
    if (daysPerWeekError) newErrors.daysPerWeek = daysPerWeekError;

    const difficultyError = validateDifficultyLevel(difficultyLevel);
    if (difficultyError) newErrors.difficultyLevel = difficultyError;

    const equipmentError = validateEquipment(equipment);
    if (equipmentError) newErrors.equipment = equipmentError;

    const goalOutcomeError = validateGoalOutcome(goalOutcome);
    if (goalOutcomeError) newErrors.goalOutcome = goalOutcomeError;

    const visibilityScopeError = validateVisibilityScope(visibilityScope);
    if (visibilityScopeError) newErrors.visibilityScope = visibilityScopeError;

    const descriptionError = validateDescription(description);
    if (descriptionError) newErrors.description = descriptionError;

    const imageError = validateImage();
    if (imageError) newErrors.image = imageError;

    const weeksError = validateWeeks();
    if (weeksError) newErrors.weeks = weeksError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let currentUniqueId = uniqueId.trim();
    if (!currentUniqueId) {
      currentUniqueId = generateUniqueIdValue();
      setUniqueId(currentUniqueId);
    }

    if (!validateAllFields(currentUniqueId)) {
      // Show error toast if image is missing
      if (errors.image) {
        showError(errors.image);
      }
      return;
    }

    // Prepare payload matching API structure
    const durationInWeeksNumber = durationInWeeks.trim()
      ? parseInt(durationInWeeks)
      : weeks.length || 0;
    const daysPerWeekNumber = daysPerWeek.trim()
      ? parseInt(daysPerWeek)
      : Math.max(0, ...(weeks.map((w) => w.days.length) || [0]));

    const payloadWeeks = weeks
      .map((week) => ({
        week: week.week,
        days: week.days.filter((d) => !!d.workoutId && d.workoutId !== "rest_day"),
        status: week.status,
        unlockDate:
          week.status === WEEKLY_PROGRAM_STATUS.LOCK && week.unlockDate
            ? new Date(week.unlockDate).toISOString()
            : undefined,
      }))
      .filter((w) => w.days.length > 0);

    const payload: any = {
      thumnail: image,
      title: title.trim(),
      subTitle: subTitle.trim() || "",
      uniqueId: currentUniqueId,
      shortDescription: " ",
      tagline: " ",
      durationInWeeks: durationInWeeksNumber,
      daysPerWeek: daysPerWeekNumber,
      difficultyLevel: difficultyLevel.map((d) => Number(d)),
      visibilityScope: Number(visibilityScope),
      equipment,
      goalOutcome,
      isPublished,
      description: description.trim(),
    };

    if (payloadWeeks.length > 0) {
      payload.weeks = payloadWeeks;
    }

    createProgram(payload);
  };

  // Add a new week
  const addNewWeek = () => {
    const maxWeeks = parseInt(durationInWeeks) || 0;
    if (weeks.length >= maxWeeks) {
      showError("Weekly duration extends for this program");
      return;
    }
    setWeeks([
      ...weeks,
      {
        week: weeks.length + 1,
        days: [{ day: 1, workoutId: "" }],
        status: WEEKLY_PROGRAM_STATUS.UNLOCK,
        unlockDate: "",
      },
    ]);
  };

  // Remove a week
  const removeWeek = (index: number) => {
    const updated = [...weeks];
    updated.splice(index, 1);
    // Renumber weeks
    const renumbered = updated.map((w, i) => ({ ...w, week: i + 1 }));
    setWeeks(renumbered);
  };

  // Add a day to a week
  const addDay = (weekIndex: number) => {
    const updated = [...weeks];
    const currentDays = weeks[weekIndex].days;
    if (currentDays.length >= 7) {
      showError("Each week can have at most 7 days");
      return;
    }
    const nextDay = currentDays.length + 1;
    updated[weekIndex].days.push({ day: nextDay, workoutId: "" });
    setWeeks(updated);
  };

  // Remove a day from a week
  const removeDay = (weekIndex: number, dayIndex: number) => {
    const updated = [...weeks];
    updated[weekIndex].days.splice(dayIndex, 1);
    // Renumber days
    updated[weekIndex].days = updated[weekIndex].days.map((d, i) => ({
      ...d,
      day: i + 1,
    }));
    setWeeks(updated);
  };

  // Update workout for a day
  const updateDayWorkout = (
    weekIndex: number,
    dayIndex: number,
    workoutId: string
  ) => {
    const updated = [...weeks];
    updated[weekIndex].days[dayIndex].workoutId = workoutId;
    setWeeks(updated);
  };

  const updateWeekStatus = (index: number, checked: boolean) => {
    const updated = [...weeks];
    updated[index].status = checked ? WEEKLY_PROGRAM_STATUS.LOCK : WEEKLY_PROGRAM_STATUS.UNLOCK;
    if (!checked) updated[index].unlockDate = "";
    setWeeks(updated);
  };

  const updateWeekUnlockDate = (index: number, date: string) => {
    const updated = [...weeks];
    updated[index].unlockDate = date;
    setWeeks(updated);
  };

  // Trim excess weeks when durationInWeeks changes
  useEffect(() => {
    const maxWeeks = parseInt(durationInWeeks) || 0;
    if (maxWeeks > 0) {
      setWeeks((currentWeeks) => {
        if (currentWeeks.length > maxWeeks) {
          const trimmed = currentWeeks.slice(0, maxWeeks);
          return trimmed.map((w, i) => ({ ...w, week: i + 1 }));
        }
        return currentWeeks;
      });
    }
  }, [durationInWeeks]);

  // Fetch equipment options
  const equipmentParams = useMemo(() => ({ type: WORKOUT_ATTRIBUTES.EQUIPMENT }), []);
  const { data: equipmentData } = useGetSettings(equipmentParams, { enabled: true });

  // Fetch goal outcome options
  const goalOutcomeParams = useMemo(() => ({ type: WORKOUT_ATTRIBUTES.GOAL_OUTCOME }), []);
  const { data: goalOutcomeData } = useGetSettings(goalOutcomeParams, { enabled: true });

  // Fetch workouts list (disabled for new programs as per requirements)
  const { data: workoutsData } = useGetRefWorkouts(
    "",
    { enabled: false }
  );

  const normalizeList = (raw: any) => {
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") {
      if (Array.isArray(raw.list)) return raw.list;
      if (Array.isArray(raw.data)) return raw.data;
    }
    return [];
  };

  const equipmentOptions = useMemo(
    () => normalizeList(equipmentData?.data),
    [equipmentData]
  );

  const goalOutcomeOptions = useMemo(
    () => normalizeList(goalOutcomeData?.data),
    [goalOutcomeData]
  );

  const workoutsList = useMemo(() => {
    const data = workoutsData?.data;
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.list)) return data.list;

    // Handle extraction from program object (for refWorkouts API)
    if (typeof data === "object") {
      const extracted: any[] = [];
      
      // 1. If the object itself has a title and ID, include it
      if ((data as any)._id && ((data as any).title || (data as any).name)) {
        extracted.push(data);
      }

      // 2. Also try to extract workouts from its weeks if they exist
      const weeksData = (data as any).weeks;
      if (Array.isArray(weeksData)) {
        weeksData.forEach((week: any) => {
          if (Array.isArray(week.days)) {
            week.days.forEach((day: any) => {
              if (day.workout && typeof day.workout === "object") {
                extracted.push(day.workout);
              }
            });
          }
        });
      }
      
      // Return unique items by _id
      return extracted.filter(
        (v, i, a) => a.findIndex((t) => t._id === v._id) === i
      );
    }

    return [];
  }, [workoutsData]);

  const equipmentOptionsMap = useMemo(
    () =>
      equipmentOptions.reduce(
        (acc: Record<string, string>, item: any) => ({
          ...acc,
          [item._id]: item.name,
        }),
        {}
      ),
    [equipmentOptions]
  );

  const goalOutcomeOptionsMap = useMemo(
    () =>
      goalOutcomeOptions.reduce(
        (acc: Record<string, string>, item: any) => ({
          ...acc,
          [item._id]: item.name,
        }),
        {}
      ),
    [goalOutcomeOptions]
  );

  return (
    <>
      <div className="main_title">
        <h1>Add Program</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-programs")}>
            Manage Programs
          </button>{" "}
          - Add Program
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            {/* Program Thumbnail */}
            <div className="control_group w_33">
              <label>Program Thumbnail*</label>

              {image ? (
                <div className="upload_image v2 no_radius">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={image}
                        alt="Uploaded Thumbnail"
                      />
                      {uploadingImage && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "4px",
                          }}
                        >
                          <span style={{ color: "white", fontWeight: "bold" }}>
                            Uploading...
                          </span>
                        </div>
                      )}
                    </figure>
                    <IconButton
                      aria-label="Remove"
                      size="small"
                      onClick={() => {
                        setImage("");
                        setFileName("");
                        setImageFile(null);
                        if (errors.image) setErrors((prev) => ({ ...prev, image: "" }));
                      }}
                      disabled={uploadingImage}
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              ) : (
                <label htmlFor="icon-button-file" className="upload_image v2 no_radius">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={handleImageChange}
                    disabled={uploadingImage}
                  />

                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/placeholder.png"
                        alt="placeholder"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
              {errors.image && (
                <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                  {errors.image}
                </p>
              )}
            </div>

            {/* Program Title */}
            <div className="control_group w_33">
              <label>Program Title*</label>
              <TextField
                placeholder="Enter Program Title"
                fullWidth
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title)
                    setErrors((prev) => ({ ...prev, title: "" }));
                }}
                onBlur={() => {
                  const err = validateTitle(title);
                  if (err) setErrors((prev) => ({ ...prev, title: err }));
                }}
                error={!!errors.title}
                helperText={errors.title}
              />
            </div>

            {/* Sub Title */}
            <div className="control_group w_33">
              <label>Sub Title</label>
              <TextField
                placeholder="Enter Sub Title"
                fullWidth
                value={subTitle}
                onChange={(e) => setSubTitle(e.target.value)}
              />
            </div>

            {/* Unique Id */}
            <div className="control_group w_33">
              <label>Unique Id*</label>
              <TextField
                placeholder="Enter 6-char code or click Auto Generate"
                fullWidth
                value={uniqueId}
                onChange={(e) => {
                  setUniqueId(e.target.value);
                  if (errors.uniqueId) setErrors((prev) => ({ ...prev, uniqueId: "" }));
                }}
                onBlur={() => {
                  const err = validateUniqueId(uniqueId);
                  if (err) setErrors((prev) => ({ ...prev, uniqueId: err }));
                }}
                error={!!errors.uniqueId}
                helperText={errors.uniqueId}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        position: "absolute !important",
                        right: "8px !important",
                        top: "50% !important",
                        transform: "translateY(-50%) !important",
                        height: "auto !important",
                        maxHeight: "none !important",
                        margin: "0 !important",
                        display: "flex !important",
                        alignItems: "center !important",
                        zIndex: 2,
                      }}
                    >
                      <Button
                        className="text_btn"
                        onClick={handleGenerateUniqueId}
                        sx={{
                          top: "0 !important",
                          minWidth: "auto !important",
                          height: "26px !important",
                          padding: "0 10px !important",
                          fontSize: "11px !important",
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "13px !important",
                          color: "#837559 !important",
                          backgroundColor: "#F1EDE7 !important",
                          lineHeight: 1,
                          boxShadow: "none !important",
                          display: "inline-flex !important",
                          alignItems: "center !important",
                          justifyContent: "center !important",
                          "&:hover": {
                            color: "#04060A !important",
                            backgroundColor: "#E5E0DA !important",
                            boxShadow: "none !important",
                          },
                        }}
                      >
                        Auto Generate
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            {/* Duration in Weeks */}
            <div className="control_group w_33">
              <label>Duration in Weeks</label>
              <TextField
                placeholder="Duration in Weeks"
                fullWidth
                type="number"
                value={durationInWeeks}
                onChange={(e) => {
                  setDurationInWeeks(e.target.value);
                  if (errors.durationInWeeks)
                    setErrors((prev) => ({ ...prev, durationInWeeks: "" }));
                }}
                onBlur={() => {
                  const err = validateDurationInWeeks(durationInWeeks);
                  if (err) setErrors((prev) => ({ ...prev, durationInWeeks: err }));
                }}
                error={!!errors.durationInWeeks}
                helperText={errors.durationInWeeks}
              />
            </div>

            {/* Days Per Week */}
            <div className="control_group w_33">
              <label>Days Per Week</label>
              <TextField
                placeholder="Days Per Week"
                fullWidth
                type="number"
                value={daysPerWeek}
                onChange={(e) => {
                  setDaysPerWeek(e.target.value);
                  if (errors.daysPerWeek)
                    setErrors((prev) => ({ ...prev, daysPerWeek: "" }));
                }}
                onBlur={() => {
                  const err = validateDaysPerWeek(daysPerWeek);
                  if (err) setErrors((prev) => ({ ...prev, daysPerWeek: err }));
                }}
                error={!!errors.daysPerWeek}
                helperText={errors.daysPerWeek}
              />
            </div>

            {/* Level */}
            <div className="control_group w_33">
              <label>Level*</label>
              <Select
                fullWidth
                multiple
                value={difficultyLevel}
                onChange={handleDifficultyLevel}
                displayEmpty
                onBlur={() => {
                  const err = validateDifficultyLevel(difficultyLevel);
                  if (err) setErrors((prev) => ({ ...prev, difficultyLevel: err }));
                }}
                renderValue={(selected) => {
                  if (!selected || (selected as string[]).length === 0) {
                    return "Select Level";
                  }
                  const map: Record<string, string> = {
                    [String(DIFFICULTY_LEVEL.BEGGINER)]: "Beginner",
                    [String(DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE)]: "Beginner-Intermediate",
                    [String(DIFFICULTY_LEVEL.INTERMEDIATE)]: "Intermediate",
                    [String(DIFFICULTY_LEVEL.ADVANCED)]: "Advanced",
                  };
                  const labels = (selected as string[]).map((v) => map[v]).filter(Boolean);
                  return labels.length ? labels.join(", ") : "Select Level";
                }}
                error={!!errors.difficultyLevel}
              >
                <MenuItem value={String(DIFFICULTY_LEVEL.BEGGINER)}>Beginner</MenuItem>
                <MenuItem value={String(DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE)}>Beginner-Intermediate</MenuItem>
                <MenuItem value={String(DIFFICULTY_LEVEL.INTERMEDIATE)}>Intermediate</MenuItem>
                <MenuItem value={String(DIFFICULTY_LEVEL.ADVANCED)}>Advanced</MenuItem>
              </Select>
              {errors.difficultyLevel && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  {errors.difficultyLevel}
                </p>
              )}
            </div>

            {/* Equipment Needed */}
            <div className="control_group w_33">
              <label>Equipment Needed*</label>
              <Select
                multiple
                fullWidth
                value={equipment}
                onChange={(event: SelectChangeEvent<string[]>) => {
                  setEquipment(typeof event.target.value === "string" ? event.target.value.split(",") : (event.target.value as string[]));
                  if (errors.equipment)
                    setErrors((prev) => ({ ...prev, equipment: "" }));
                }}
                onBlur={() => {
                  const err = validateEquipment(equipment);
                  if (err) setErrors((prev) => ({ ...prev, equipment: err }));
                }}
                renderValue={(selected) =>
                  selected.length
                    ? selected
                      .map((id) => equipmentOptionsMap[id] || id)
                      .join(", ")
                    : "Select Equipment"
                }
                displayEmpty
                error={!!errors.equipment}
              >
                {equipmentOptions.map((item: any) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.equipment && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  {errors.equipment}
                </p>
              )}
            </div>

            {/* Goal */}
            <div className="control_group w_33">
              <label>Goal*</label>
              <Select
                multiple
                fullWidth
                value={goalOutcome}
                onChange={(event: SelectChangeEvent<string[]>) => {
                  setGoalOutcome(typeof event.target.value === "string" ? event.target.value.split(",") : (event.target.value as string[]));
                  if (errors.goalOutcome)
                    setErrors((prev) => ({ ...prev, goalOutcome: "" }));
                }}
                onBlur={() => {
                  const err = validateGoalOutcome(goalOutcome);
                  if (err) setErrors((prev) => ({ ...prev, goalOutcome: err }));
                }}
                renderValue={(selected) =>
                  selected.length
                    ? selected
                      .map((id) => goalOutcomeOptionsMap[id] || id)
                      .join(", ")
                    : "Select Goal"
                }
                displayEmpty
                error={!!errors.goalOutcome}
              >
                {goalOutcomeOptions.map((item: any) => (
                  <MenuItem key={item._id} value={item._id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.goalOutcome && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  {errors.goalOutcome}
                </p>
              )}
            </div>

            {/* Visibility Scope */}
            <div className="control_group w_33">
              <label>Visibility Scope*</label>
              <Select
                fullWidth
                value={visibilityScope || "def"}
                onChange={handleVisibilityScope}
                displayEmpty
                onBlur={() => {
                  const err = validateVisibilityScope(visibilityScope);
                  if (err) setErrors((prev) => ({ ...prev, visibilityScope: err }));
                }}
                error={!!errors.visibilityScope}
              >
                <MenuItem disabled value="def">
                  Select Visibility Scope
                </MenuItem>
                <MenuItem value={String(VISIBILITY_SCOPE.DEMAND)}>Demand</MenuItem>
                <MenuItem value={String(VISIBILITY_SCOPE.PROGRAM)}>Program</MenuItem>
                <MenuItem value={String(VISIBILITY_SCOPE.BOTH)}>Both</MenuItem>
              </Select>
              {errors.visibilityScope && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  {errors.visibilityScope}
                </p>
              )}
            </div>

            {/* Is Published */}
            <div className="control_group w_33">
              <label>Publish</label>
              <Select
                fullWidth
                value={isPublished ? "true" : "false"}
                onChange={(e) => setIsPublished(e.target.value === "true")}
              >
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </div>

            {/* Description */}
            <div className="control_group w_100">
              <label>Description*</label>
              <EditText
                key="program-add"
                content={description}
                setContent={(value: string) => {
                  setDescription(value);
                  if (errors.description) {
                    setErrors((prev) => ({ ...prev, description: "" }));
                  }
                }}
              />
              {errors.description && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  {errors.description}
                </p>
              )}
            </div>

            {/* -------------------- WEEK BUILDER -------------------- */}

            <div className="w_100">
              <div
                className="main_title"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <h1>Program Weeks & Workouts</h1>
                {/* Add New Week Button */}
                <div>
                  <Button
                    startIcon={<AddIcon />}
                    className="btn btn_secondary"
                    onClick={addNewWeek}
                    disabled={
                      !durationInWeeks ||
                      weeks.length >= (parseInt(durationInWeeks) || 0)
                    }
                  >
                    Add New Week
                  </Button>
                </div>
              </div>
              {errors.weeks && (
                <p style={{ color: "red", fontSize: "14px", marginTop: "5px" }}>
                  {errors.weeks}
                </p>
              )}
              {weeks.map((week, weekIndex) => (
                <Accordion key={weekIndex} sx={{ marginBottom: "15px", borderRadius: "8px", border: "1px solid #e9ecef", boxShadow: "none", '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                      <h3 style={{ margin: 0 }}>Week {week.week}</h3>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); removeWeek(weekIndex); }}
                        disabled={weeks.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                  <div className="week_controls" style={{
                    padding: '15px 20px',
                    marginBottom: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      flexWrap: 'wrap'
                    }}>
                      <div>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={week.status === WEEKLY_PROGRAM_STATUS.LOCK}
                              onChange={(e) => updateWeekStatus(weekIndex, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={<span style={{ fontWeight: 500 }}>Lock Week</span>}
                        />
                      </div>

                      {week.status === WEEKLY_PROGRAM_STATUS.LOCK && (
                        <div style={{ flex: '1', minWidth: '250px', maxWidth: '350px' }}>
                          <TextField
                            type="date"
                            label="Unlock Date"
                            fullWidth
                            size="small"
                            value={week.unlockDate}
                            onChange={(e) => updateWeekUnlockDate(weekIndex, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form">
                    <div className="gap_p">
                      {/* Days for this Week */}
                      <div className="control_group w_100">
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            alignItems: "center",
                            marginBottom: "10px",
                          }}
                        >
                          <label>Days for Week {week.week}</label>
                          <Button
                            startIcon={<AddIcon />}
                            className="btn btn_primary sm"
                            onClick={() => addDay(weekIndex)}
                            disabled={week.days.length >= 7}
                          >
                            Add Day
                          </Button>
                        </div>

                        {week.days.map((day, dayIndex) => (
                          <div className="control_group" key={dayIndex} style={{ marginBottom: "10px" }}>
                            <div className="gap_p">
                              <div className="control_group w_25">
                                <label>Day {day.day}</label>
                                <TextField
                                  fullWidth
                                  type="number"
                                  value={day.day}
                                  disabled
                                />
                              </div>
                              <div className="control_group w_70">
                                <label>Workout*</label>
                                <Select
                                  fullWidth
                                  value={day.workoutId || "def"}
                                  onChange={(e) => updateDayWorkout(weekIndex, dayIndex, e.target.value)}
                                  displayEmpty
                                >
                                  <MenuItem disabled value="def">
                                    Select Workout
                                  </MenuItem>
                                  <MenuItem value="rest_day">Rest Day</MenuItem>
                                  {workoutsList.map((workout: any) => (
                                    <MenuItem key={workout._id} value={workout._id}>
                                      {`${workout.refCode ? workout.refCode + " - " : ""}${workout.title || workout.name || workout._id}`}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </div>
                              <div className="control_group w_5">
                                <label>&nbsp;</label>
                                <IconButton
                                  size="small"
                                  onClick={() => removeDay(weekIndex, dayIndex)}
                                  disabled={week.days.length === 1}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          </div>

          <div className="form_btn">
            <Button
              className="btn btn_primary"
              onClick={handleSubmit}
              disabled={isCreating}
            >
              {isCreating ? "Saving..." : "Save Program"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProgram;
